import { MutableRefObject, RefObject, useEffect } from "react";

/** Minimum user-scroll distance (px) before the editor auto-collapses. */
export const AUTO_COLLAPSE_SCROLL_DELTA = 40;

/**
 * Collapses the sticky editor while the user reads the thread (mockup
 * behaviour): a user-initiated scroll of the chat log beyond
 * {@link AUTO_COLLAPSE_SCROLL_DELTA}, or a click in the chat log outside the
 * draft cards, collapses the editor to its strip. Programmatic scrolls
 * (scroll-to-bottom after draft changes) are ignored via
 * `programmaticScrollRef`, and clicks on draft cards keep the editor open —
 * activating a draft is an edit intent, not a reading intent.
 */
export const useEditorAutoCollapse = ({
	scrollRef,
	programmaticScrollRef,
	enabled,
	onCollapse,
}: {
	scrollRef: RefObject<HTMLDivElement>;
	programmaticScrollRef: MutableRefObject<boolean>;
	enabled: boolean;
	onCollapse: () => void;
}) => {
	useEffect(() => {
		const container = scrollRef.current;

		if (!container || !enabled) {
			return;
		}

		let lastScrollTop = container.scrollTop;

		const handleScroll = () => {
			if (programmaticScrollRef.current) {
				lastScrollTop = container.scrollTop;

				return;
			}

			if (Math.abs(container.scrollTop - lastScrollTop) > AUTO_COLLAPSE_SCROLL_DELTA) {
				lastScrollTop = container.scrollTop;
				onCollapse();
			}
		};

		const handleClick = (event: MouseEvent) => {
			if ((event.target as HTMLElement).closest("[data-draft-card]")) {
				return;
			}

			onCollapse();
		};

		container.addEventListener("scroll", handleScroll);
		container.addEventListener("click", handleClick);

		return () => {
			container.removeEventListener("scroll", handleScroll);
			container.removeEventListener("click", handleClick);
		};
	}, [scrollRef, programmaticScrollRef, enabled, onCollapse]);
};
